<template>
  <div class="c-form-item-multi-input-ex">
    <div v-if="!noHeader" class="header">
      <div class="checkbox-con">
        <c-checkbox
          v-if="inputPropOptions"
          class="checkbox"
          check-type="radio"
          v-model="inputProp"
          :options="inputPropOptions"
          :style="checkboxStyle"
          v-bind="{ ...innerAttrs?.checkbox }"
        />
        <span v-else-if="inputLabel">{{ inputLabel }}</span>
      </div>
      <div class="btn-clear">
        <el-button @click="handleClear">清空</el-button>
      </div>
    </div>
    <div class="body">
      <el-input
        type="textarea"
        v-model="inputValue"
        :rows="rows"
        :disabled="disabled"
        :placeholder="placeholder"
        :resize="resize"
        :autosize="autosize"
        @keyup="handleKeyup"
        @blur="handleBlur"
        :style="inputStyle"
        v-bind="{ ...innerAttrs?.input }"
      />
    </div>
  </div>
</template>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<script setup lang="ts">
import { _, computed, ref, watch, nextTick, useCurrentAppInstance } from '@zto/zpage'

import { useFormItem } from '@zto/zpage-ui-element'

const props = withDefaults(
  defineProps<{
    model: Record<string, any>
    prop?: string // 结算编号
    inputLabel?: string // 输入标签
    inputProps?: Record<string, string>
    exceededMessage?: string | boolean // 操作消息
    noHeader?: boolean // 不显示头部
    maxLines?: number // 支持最大条数
    disabled?: boolean
    inputStyle?: string | object // input样式
    checkboxStyle?: string | object // checkbox样式
    placeholder?: string
    rows?: number // 行数
    autosize?: any // 是否自适应内容高度， boolean|{ minRows: 2, maxRows: 6 }
    resize?: string // 用户是否可以缩放
    innerAttrs?: Record<string, any> // 内部元素属性
  }>(),
  {
    noHeader: false,
    disabled: false,
    maxLines: 200,
    rows: 3,
    autosize: false,
    resize: 'none',
    placeholder: '请输入',
    exceededMessage: ''
  }
)

useFormItem(props)

const app = useCurrentAppInstance()

const inputValue = ref<string>('')
const inputProp = ref<string>(props.prop || '')

const inputPropOptions = computed(() => {
  if (!props.inputProps) return null

  const options = Object.keys(props.inputProps).reduce((col, key) => {
    const label = (props.inputProps as any)[key]
    col.push({ label, value: key })
    return col
  }, [] as Record<string, string>[])

  return options
})

const innerInputProp = computed(() => {
  return inputProp.value || props.prop
})

watch(
  () => innerInputProp.value,
  () => {
    triggerChange()
  }
)

// 监视 innerInputProp 值，防止reset出现空值
watch(
  () => innerInputProp.value && props.model[innerInputProp.value],
  (cur, old) => {
    if (!innerInputProp.value) return

    // 防止发布后出现死循环，此死循环只有发布后才会出现
    if (_.deepEqual(cur, old)) return

    // 默认传入string
    if (_.isString(cur)) {
      inputValue.value = cur.trim()
      triggerChange()
      return
    }

    if (Array.isArray(cur)) {
      const oldStr = old || [].join()

      if (cur.join() === oldStr) return

      nextTick(() => {
        props.model[innerInputProp.value!] = cur.filter(it => !!it)

        if (!cur.length) {
          inputValue.value = ''
        } else {
          inputValue.value = cur.join('\n')
        }
      })
    }
  },
  { immediate: true }
)

function handleKeyup(e: KeyboardEvent) {
  e.stopPropagation()
}

function handleClear() {
  inputValue.value = ''
  triggerChange()
}

function handleBlur() {
  triggerChange()
}

function triggerChange() {
  if (!innerInputProp.value) return

  let inputArr = inputValue.value
    .split(/,|\n/)
    .map(it => it.trim())
    .filter(it => !!it)

  // 验证最大条数
  const { Message } = app.useMessage()

  let inputLength = inputArr.length

  if (inputLength > props.maxLines) {
    let message = props.exceededMessage

    if (!message && message !== false) {
      message = `${props.inputLabel || ''}数量超过${props.maxLines}条，将截取前${props.maxLines}条数据`
    } else if (message) {
      message = app.filter(message, { inputLength, inputArr, ...props })
    }

    if (message) Message.warning(message)

    inputArr = inputArr.slice(0, props.maxLines)
    inputValue.value = inputArr.join('\n')
  }

  // 清空原代码
  if (inputPropOptions.value) {
    inputPropOptions.value.forEach(it => {
      props.model[it.value] = undefined
    })
  }

  props.model[innerInputProp.value] = inputArr
}
</script>

<style lang="scss" scoped>
.c-form-item-multi-input-ex {
  & > .header {
    display: flex;
    padding-bottom: 10px;
  }

  .checkbox-con {
    flex: 1;
    :deep(.el-radio-group) {
      display: flex;
      flex-direction: column;
    }
  }
}
</style>
